/**
 * Punch List API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { ApiResponse } from '../types';
import type {
  BulkAssignRequest,
  BulkVerifyRequest,
  ContractorReport,
  CreatePunchItemRequest,
  CreateWalkRequest,
  MarkReadyRequest,
  PunchItem,
  PunchListQueryParams,
  PunchListResponse,
  PunchListStats,
  PunchWalk,
  RejectRequest,
  SummaryReport,
  UpdatePunchItemRequest,
  VerifyRequest,
} from '../types/punchItem';
import { baseApi } from './api';

export const punchListApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // PUNCH ITEM CRUD
    // ============================================================================

    getPunchItems: builder.query<
      ApiResponse<PunchListResponse>,
      { projectId: string; params?: PunchListQueryParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/punch-list`,
        params,
      }),
      providesTags: ['PunchItem'],
    }),

    getPunchItem: builder.query<ApiResponse<PunchItem>, { projectId: string; id: string }>({
      query: ({ projectId, id }) => `/projects/${projectId}/punch-list/${id}`,
      providesTags: (_result, _error, { id }) => [{ type: 'PunchItem', id }],
    }),

    createPunchItem: builder.mutation<
      ApiResponse<PunchItem>,
      { projectId: string; data: CreatePunchItemRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/punch-list`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PunchItem'],
    }),

    updatePunchItem: builder.mutation<
      ApiResponse<PunchItem>,
      { projectId: string; id: string; data: UpdatePunchItemRequest }
    >({
      query: ({ projectId, id, data }) => ({
        url: `/projects/${projectId}/punch-list/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => ['PunchItem', { type: 'PunchItem', id }],
    }),

    deletePunchItem: builder.mutation<ApiResponse<void>, { projectId: string; id: string }>({
      query: ({ projectId, id }) => ({
        url: `/projects/${projectId}/punch-list/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PunchItem'],
    }),

    // ============================================================================
    // STATS
    // ============================================================================

    getPunchListStats: builder.query<ApiResponse<PunchListStats>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/punch-list/stats`,
      providesTags: ['PunchItem'],
    }),

    // ============================================================================
    // STATUS TRANSITIONS
    // ============================================================================

    startWork: builder.mutation<ApiResponse<PunchItem>, { projectId: string; id: string }>({
      query: ({ projectId, id }) => ({
        url: `/projects/${projectId}/punch-list/${id}/start`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { id }) => ['PunchItem', { type: 'PunchItem', id }],
    }),

    markReady: builder.mutation<
      ApiResponse<PunchItem>,
      { projectId: string; id: string; data: MarkReadyRequest }
    >({
      query: ({ projectId, id, data }) => ({
        url: `/projects/${projectId}/punch-list/${id}/ready`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => ['PunchItem', { type: 'PunchItem', id }],
    }),

    verifyPunchItem: builder.mutation<
      ApiResponse<PunchItem>,
      { projectId: string; id: string; data: VerifyRequest }
    >({
      query: ({ projectId, id, data }) => ({
        url: `/projects/${projectId}/punch-list/${id}/verify`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => ['PunchItem', { type: 'PunchItem', id }],
    }),

    rejectPunchItem: builder.mutation<
      ApiResponse<PunchItem>,
      { projectId: string; id: string; data: RejectRequest }
    >({
      query: ({ projectId, id, data }) => ({
        url: `/projects/${projectId}/punch-list/${id}/reject`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => ['PunchItem', { type: 'PunchItem', id }],
    }),

    closePunchItem: builder.mutation<ApiResponse<PunchItem>, { projectId: string; id: string }>({
      query: ({ projectId, id }) => ({
        url: `/projects/${projectId}/punch-list/${id}/close`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { id }) => ['PunchItem', { type: 'PunchItem', id }],
    }),

    // ============================================================================
    // BULK OPERATIONS
    // ============================================================================

    bulkAssignPunchItems: builder.mutation<
      ApiResponse<{ modifiedCount: number }>,
      { projectId: string; data: BulkAssignRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/punch-list/bulk/assign`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['PunchItem'],
    }),

    bulkVerifyPunchItems: builder.mutation<
      ApiResponse<{ modifiedCount: number }>,
      { projectId: string; data: BulkVerifyRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/punch-list/bulk/verify`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['PunchItem'],
    }),

    // ============================================================================
    // WALKS
    // ============================================================================

    getPunchWalks: builder.query<ApiResponse<PunchWalk[]>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/punch-list/walks`,
      providesTags: ['PunchItem'],
    }),

    createPunchWalk: builder.mutation<
      ApiResponse<PunchWalk>,
      { projectId: string; data: CreateWalkRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/punch-list/walks`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PunchItem'],
    }),

    startPunchWalk: builder.mutation<ApiResponse<PunchWalk>, { projectId: string; walkId: string }>(
      {
        query: ({ projectId, walkId }) => ({
          url: `/projects/${projectId}/punch-list/walks/${walkId}/start`,
          method: 'POST',
        }),
        invalidatesTags: ['PunchItem'],
      }
    ),

    completePunchWalk: builder.mutation<
      ApiResponse<PunchWalk>,
      { projectId: string; walkId: string }
    >({
      query: ({ projectId, walkId }) => ({
        url: `/projects/${projectId}/punch-list/walks/${walkId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: ['PunchItem'],
    }),

    // ============================================================================
    // REPORTS
    // ============================================================================

    getPunchListSummaryReport: builder.query<ApiResponse<SummaryReport>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/punch-list/report/summary`,
      providesTags: ['PunchItem'],
    }),

    getPunchListContractorReport: builder.query<
      ApiResponse<ContractorReport>,
      { projectId: string; contractorId: string }
    >({
      query: ({ projectId, contractorId }) =>
        `/projects/${projectId}/punch-list/report/contractor/${contractorId}`,
      providesTags: ['PunchItem'],
    }),
  }),
});

export const {
  useGetPunchItemsQuery,
  useGetPunchItemQuery,
  useCreatePunchItemMutation,
  useUpdatePunchItemMutation,
  useDeletePunchItemMutation,
  useGetPunchListStatsQuery,
  useStartWorkMutation,
  useMarkReadyMutation,
  useVerifyPunchItemMutation,
  useRejectPunchItemMutation,
  useClosePunchItemMutation,
  useBulkAssignPunchItemsMutation,
  useBulkVerifyPunchItemsMutation,
  useGetPunchWalksQuery,
  useCreatePunchWalkMutation,
  useStartPunchWalkMutation,
  useCompletePunchWalkMutation,
  useGetPunchListSummaryReportQuery,
  useGetPunchListContractorReportQuery,
} = punchListApi;
